import { useEffect, useMemo, useState } from 'react';
import {
  usePortalCancelAppointment,
  usePortalRescheduleAppointment,
  type PortalAppointmentItem,
} from '@/hooks/usePortalAppointments';
import { useAvailableSlotsWeek } from '@/hooks/useAvailableSlotsWeek';
import { useBookableServices, type AvailableSlot } from '@/hooks/usePublicBooking';
import {
  catalogClinicianToSchedule,
  exceptionFromPublic,
  listBookablePractitionerDates,
} from '@/lib/clinicHours';
import { cliniciansForBookableService } from '@/lib/serviceBookingEligibility';
import {
  addCivilDays,
  clinicCivilDate,
  firstBookableWeekMonday,
  formatPortalDateTime,
  mondayOf,
  weekdaysMondayToFriday,
} from '@/lib/portalFormat';
import { PortalIcon } from '@/components/portal/PortalIcon';
import { WeekSlotPicker } from '@/components/portal/WeekSlotPicker';

export function PortalCancelModal({
  appointment,
  onClose,
}: {
  appointment: PortalAppointmentItem;
  onClose: () => void;
}) {
  const cancelMutation = usePortalCancelAppointment();
  const [reason, setReason] = useState('');

  const handleConfirm = async () => {
    try {
      await cancelMutation.mutateAsync({
        appointment_id: appointment.id,
        reason: reason.trim() || 'Aflyst af patienten via portalen',
      });
      onClose();
    } catch (err) {
      console.error('Failed to cancel appointment:', err);
    }
  };

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="cancel-title">
        <div className="grab" />
        <div className="mh">
          <div>
            <h2 id="cancel-title">Aflys tid</h2>
            <div className="help">
              Nu: {formatPortalDateTime(appointment.starts_at)}
              {appointment.service_name ? ` · ${appointment.service_name}` : ''}
              {appointment.clinician_name ? ` hos ${appointment.clinician_name}` : ''}
            </div>
          </div>
          <button type="button" className="btn btn-tertiary btn-icon x" aria-label="Luk" onClick={onClose}>
            <PortalIcon name="x" />
          </button>
        </div>
        <div className="mb">
          <div className="field">
            <label htmlFor="cancel-reason">Årsag (valgfri)</label>
            <textarea
              id="cancel-reason"
              className="input"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Skriv gerne hvorfor, så klinikken kan hjælpe"
            />
          </div>
          {cancelMutation.isError ? (
            <p className="help" style={{ color: 'var(--red-ink)', marginTop: 12 }}>
              Tiden kunne ikke aflyses. Prøv igen, eller skriv til klinikken.
            </p>
          ) : null}
        </div>
        <div className="mf">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={cancelMutation.isPending}>
            Behold tiden
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => void handleConfirm()}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? 'Aflyser…' : 'Aflys tid'}
          </button>
        </div>
      </div>
    </>
  );
}

export function PortalRescheduleModal({
  appointment,
  onClose,
}: {
  appointment: PortalAppointmentItem;
  onClose: () => void;
}) {
  const reschedule = usePortalRescheduleAppointment();
  const today = clinicCivilDate(new Date());
  const [weekMonday, setWeekMonday] = useState(() => mondayOf(today));
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const canPickSlots = Boolean(appointment.clinic_slug && appointment.service_id);
  const { data: bookingData } = useBookableServices(canPickSlots ? appointment.clinic_slug : null);
  const days = weekdaysMondayToFriday(weekMonday);

  const closedDates = useMemo(() => {
    return new Set(
      (bookingData?.settings.hours_exceptions ?? [])
        .filter((row) => row.exception_type === 'closed')
        .map((row) => row.date),
    );
  }, [bookingData]);

  const bookableCivils = useMemo(() => {
    if (!bookingData) return [];
    const exceptionsByDate = new Map(
      (bookingData.settings.hours_exceptions ?? []).map((row) => [row.date, exceptionFromPublic(row)]),
    );
    const service = bookingData.services.find((item) => item.id === appointment.service_id);
    const eligible = cliniciansForBookableService(
      bookingData.clinicians,
      service?.eligible_practitioner_ids,
    );
    const clinicians = appointment.clinician_id
      ? eligible.filter((clinician) => clinician.id === appointment.clinician_id)
      : eligible;
    return listBookablePractitionerDates({
      practitioners: clinicians.map(catalogClinicianToSchedule),
      clinicExceptions: exceptionsByDate,
      advanceDays: bookingData.settings.advance_days ?? 60,
    }).map((day) => clinicCivilDate(day));
  }, [bookingData, appointment.clinician_id, appointment.service_id]);

  useEffect(() => {
    if (bookableCivils.length === 0) return;
    const firstWeek = firstBookableWeekMonday(bookableCivils, mondayOf(today));
    setWeekMonday((current) => (current === mondayOf(today) && firstWeek !== current ? firstWeek : current));
  }, [bookableCivils.join(','), today]);

  const { slotsByDate } = useAvailableSlotsWeek(
    appointment.clinic_slug,
    appointment.service_id,
    appointment.clinician_id,
    days,
  );

  const handleConfirm = async () => {
    if (!selectedSlot) return;
    try {
      await reschedule.mutateAsync({
        appointment_id: appointment.id,
        starts_at: selectedSlot.start,
        ends_at: selectedSlot.end,
        clinician_id: selectedSlot.clinician_id,
      });
      onClose();
    } catch (err) {
      console.error('Failed to reschedule appointment:', err);
    }
  };

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="mt">
        <div className="grab" />
        <div className="mh">
          <div>
            <h2 id="mt">Flyt tid</h2>
            <div className="help">
              Nu: {formatPortalDateTime(appointment.starts_at)}
              {appointment.service_name ? ` · ${appointment.service_name}` : ''}
              {appointment.clinician_name ? ` hos ${appointment.clinician_name}` : ''}
            </div>
          </div>
          <button type="button" className="btn btn-tertiary btn-icon x" aria-label="Luk" onClick={onClose}>
            <PortalIcon name="x" />
          </button>
        </div>
        <div className="mb">
          {canPickSlots ? (
            <>
              <WeekSlotPicker
                days={days}
                weekMonday={weekMonday}
                slotsByDate={slotsByDate}
                closedDates={closedDates}
                selectedStart={selectedSlot?.start ?? null}
                onSelect={setSelectedSlot}
                onPrevWeek={() => setWeekMonday((value) => addCivilDays(value, -7))}
                onNextWeek={() => setWeekMonday((value) => addCivilDays(value, 7))}
                canGoPrev={weekMonday > mondayOf(today)}
                today={today}
              />
              {selectedSlot ? (
                <div className="notice teal" style={{ marginTop: 16 }}>
                  <PortalIcon name="info" size="sm" />
                  <span>
                    Ny tid: <b>{formatPortalDateTime(selectedSlot.start)}</b>
                    {appointment.clinician_name ? ` hos ${appointment.clinician_name}` : ''}. Du får
                    en bekræftelse på e-mail.
                  </span>
                </div>
              ) : null}
            </>
          ) : (
            <p className="help">Tiden kan ikke flyttes her. Skriv til klinikken.</p>
          )}
          {reschedule.isError ? (
            <p className="help" style={{ color: 'var(--red-ink)', marginTop: 12 }}>
              Tiden kunne ikke flyttes. Prøv en anden tid, eller skriv til klinikken.
            </p>
          ) : null}
        </div>
        <div className="mf">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Behold nuværende tid
          </button>
          {canPickSlots ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!selectedSlot || reschedule.isPending}
              onClick={() => void handleConfirm()}
            >
              {reschedule.isPending
                ? 'Flytter…'
                : selectedSlot
                  ? `Flyt til ${formatPortalDateTime(selectedSlot.start)}`
                  : 'Vælg en tid'}
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}
